import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionList } from '../../../shared/components/DescriptionList/DescriptionList';
import type { AlertRule } from '../../alert-rules-table/services/AlertRule.entity';
import type { AlertRulesService } from '../../alert-rules-table/services/AlertRulesService';
import type { TAlertRule } from '../../types';

type Props = {
  service: AlertRulesService;
  entity: AlertRule;
};
type ContextField = {
  field: string;
  message: string;
  getContextValue: (id: number | string, service: AlertRulesService) => string | undefined;
  translateValue?: boolean;
};
const contextFields: ContextField[] = [
  {
    field: 'stageIds',
    message: 'alertsManagerWidget:contextPanel.fields.stage',
    getContextValue: (id, service) =>
      service.workClassifierStageTypes.find((stage) => stage.id === id)?.data['name']?.toString(),
  },
  {
    field: 'wellboreTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.wellboreType',
    getContextValue: (id, service) =>
      service.wellboreTypes.find((wellboreType) => wellboreType.id === id)?.data['customName']?.toString(),
  },
  {
    field: 'trajectoryTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.trajectoryType',
    getContextValue: (id, service) =>
      service.trajectoryTypes.find((trajectoryType) => trajectoryType.id === id)?.data['name']?.toString(),
  },
  {
    field: 'sectionTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.section',
    getContextValue: (id, service) =>
      service.sectionTypes.find((sectionType) => sectionType.id === id)?.data['name']?.toString(),
  },
  {
    field: 'stratUnitTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.stratUnit',
    getContextValue: (id, service) =>
      service.stratUnitTypes.find((stratUnitType) => stratUnitType.id === id)?.data['name']?.toString(),
  },
  {
    field: 'reservoirIds',
    message: 'alertsManagerWidget:contextPanel.fields.formation',
    getContextValue: (id, service) =>
      service.reservoirTypes.find((reservoirType) => reservoirType.id === id)?.data['name']?.toString(),
  },
  {
    field: 'runTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.run',
    getContextValue: (id, service) => service.runTypes.find((runType) => runType.id === id)?.data['name']?.toString(),
  },
  {
    field: 'holeTypes',
    message: 'alertsManagerWidget:contextPanel.fields.holeType',
    getContextValue: (id) => `alertsManagerWidget:autoAnomalies.holeType.${id}`,
    translateValue: true,
  },
  {
    field: 'operationTypeIds',
    message: 'alertsManagerWidget:contextPanel.fields.operation',
    getContextValue: (id, service) =>
      service.autosupervisorOperations.find((operationType) => operationType.id === id)?.data['name']?.toString(),
  },
];

export function ContextDetails({ service, entity }: Props) {
  const { t } = useTranslation();
  const getContextIds = useCallback(
    (field: ContextField) => {
      const ids = entity.data[field.field as keyof TAlertRule] as (number | string)[];
      return ids;
    },
    [entity],
  );

  const items = useMemo(() => {
    return contextFields
      .map((field) => {
        const ids = getContextIds(field);
        if (ids.length === 0) return null;
        const value = ids
          .map((id) => field.getContextValue(id, service))
          .filter((v): v is string => !!v)
          .map((v) => (field.translateValue ? t(v) : v))
          .join(', ');
        return { term: t(field.message), value };
      })
      .filter((item): item is { term: string; value: string } => item !== null);
  }, [getContextIds, service, t]);

  return <DescriptionList items={items} />;
}
